import { Injectable } from '@angular/core';

import { UrlConstants, API_ENDPOINTS } from '@varsity/commons';
import {
    HttpHelperService, LoggingService, User,
    UserCreate, TokenService, InstituteSearchResult,
    AccountAvailableActions, UserUpdateInfo, PasswordPostObject,
    TempPassObject, Receipt, CustomerReceipt,
    NotificationToken, UserNotification, DirectLinkTickets
} from '@varsity/services';
import { Observable, Subscription } from 'rxjs';

/*
  Generated class for the TokenService provider.
  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class AccountService {
    
    constructor(
        private httpHelperService: HttpHelperService,
        private loggingService: LoggingService,
        private tokenService: TokenService
    ) {

        this.loggingService.log('Hello AccountService Provider');
    }

    TRLogin(userID: string, password: string, lookupOnlyFlag: boolean, notificationToken: NotificationToken): Observable<User> {
        this.loggingService.debug('login user');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.trLogin;

        let promise = this.httpHelperService
            .post<User>(url,
                {
                    LookupOnlyFlag: lookupOnlyFlag,
                    Password: password,
                    UserID: userID,
                    NotificationToken: notificationToken
                },
                {
                    params: {
                        "X-Token": this.tokenService.getToken()
                    }
                });

        return promise;
    }

    TRLogOut(notificationToken: string): Observable<any> {
        this.loggingService.debug('logout user');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.trLogout;

        let promise = this.httpHelperService
            .get<any>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        MobileFlag: "true",
                        NotificationToken: notificationToken
                    }
                });

        return promise;
    }

    createCustomer(user: UserCreate): Observable<{ ResultMessage: string }> {
        this.loggingService.debug('creating customer');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.createCustomer;

        let promise = this.httpHelperService
            .post<{ ResultMessage: string }>(url,
            Object.assign({}, user, { "SchoolAccountFlag": true }),
                {
                    params: {
                        "X-Token": this.tokenService.getToken()
                    }
                });

        return promise;
    }

    getSponsors(): Observable<InstituteSearchResult[]> {
        this.loggingService.debug('getting list of sponsors');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.sponsorsUrl;

        let promise = this.httpHelperService
            .get<InstituteSearchResult[]>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "SchoolFlag": "true"
                    }
                });

        return promise;
    }

    getActions(sponsorID: number, dbName: string): Observable<AccountAvailableActions[]> {
        this.loggingService.debug('getting list of actions');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.actionsUrl;

        let promise = this.httpHelperService
            .get<AccountAvailableActions[]>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "SponsorID": sponsorID,
                        "DBName": dbName
                    }
                });

        return promise;
    }


    updateAccount(accountInfo: UserUpdateInfo): Observable<boolean> {
        this.loggingService.debug('updating user account...');

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.updateAccount;

        let promise = this.httpHelperService
            .post<boolean>(url,
            accountInfo,
                {
                    params: {
                        "X-Token": this.tokenService.getToken()
                    }
                });

        return promise;
    }

    changePassword(passPostObject: PasswordPostObject): Observable<boolean> {
        this.loggingService.debug("Changing user password");

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.changePassword;

        let promise = this.httpHelperService
            .post<boolean>(url, passPostObject,
                {
                    params: {
                        "X-Token": this.tokenService.getToken()
                    }
            });
        return promise;
    }

    sendTempPassword(tempPassPostOject: TempPassObject): Observable<boolean> {
        this.loggingService.debug("Sending temporary password");

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.forgotPassword;

        let promise = this.httpHelperService
            .post<boolean>(url, tempPassPostOject,
                {
                    params: {
                        "X-Token": this.tokenService.getToken()
                    }
            });

        return promise;
    }

    getReceipt(confirmationID, sponsorID): Observable<Receipt> {
        this.loggingService.debug("Get receipt");

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.receiptUrl;

        let promise = this.httpHelperService
            .get<Receipt>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        'ConfirmationID': confirmationID,
                        'SponsorID': sponsorID,
                        'MobileFlag': 'true',
                        'SubscriptionFlag': 'true'
                    }
                });

        return promise;
    }

    getCustomerReceipts(): Observable<CustomerReceipt[]> {
        this.loggingService.debug("Get list of receipts");

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.customerReceiptsUrl;

        let promise = this.httpHelperService
            .get<CustomerReceipt[]>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken()
                    }
                });

        return promise;
    }

    getUserNotifications(): Observable<UserNotification[]> {
        this.loggingService.debug("Getting notifications");

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.accountNotifications;

        let promise = this.httpHelperService
            .get<UserNotification[]>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken()
                    }
                });

        return promise;
    };

    getUserNotificationsCount(): Observable<number> {
        this.loggingService.debug("Getting number of notifications");

        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.accountNotificationsCount;

        let promise = this.httpHelperService
            .get<number>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "CountFlag": true
                    }
                });

        return promise;
    }
    

    updateNotification(notificationId: number, updateType: string): Observable<boolean> {
        this.loggingService.debug("Updating for " + updateType);
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.updateNotification;

        let promise = this.httpHelperService
            .get<boolean>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "NotificationID": notificationId,
                        "updateType": updateType
                    }
                });

        return promise;
    };

    getDirectTextTickets(validationToken: string, confirmationID: string, eventID: string, displayFlag: boolean): Observable<DirectLinkTickets> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.directTextTickets;
        let promise = this.httpHelperService
            .get<DirectLinkTickets>(url,
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "Token": validationToken,
                        "CID": confirmationID,
                        "EID": eventID,
                        "DisplayFlag": displayFlag
                    }
                });


        return promise;
    };

    validatePhoneNumber(phoneNumber: string): { success: boolean, error?: string } {
        var pn = phoneNumber;
        var numberPattern = /\({0,1}\d{3}\){0,1}\-{0,1}\d{3}-{0,1}\d{4}/g;
        var originalPN = phoneNumber.replace(/\ /g, '');
        let phoneNumberMatch = originalPN.match(numberPattern);
        if (phoneNumberMatch) {
            phoneNumber = phoneNumberMatch.join("");
        }

        if (!phoneNumber || originalPN != phoneNumber) {
            return { success: false, error: 'Phone number "' + pn + '" is not valid. Ex: (123)456-7890, 1234567890, 123-456-7890' };
        } else {
            // keep only the digits for this api call
            numberPattern = /\d+/g;
            phoneNumberMatch = phoneNumber.match(numberPattern);
            if (phoneNumberMatch) {
                phoneNumber = phoneNumberMatch.join("");
            }

            if (phoneNumber.length == 10) {
                return {
                    success: true
                };
            } else {
                return { success: false, error: 'Phone number "' + pn + '" must contain 10 digits. Ex: (123)456-7890, 1234567890, 123-456-7890' };
            }
        }
    }

    parsePhoneNumber(phoneNumber: string): string {
        var pn = phoneNumber;
        var numberPattern = /\({0,1}\d{3}\){0,1}\-{0,1}\d{3}-{0,1}\d{4}/g;
        var originalPN = phoneNumber.replace(/\ /g, '');
        let phoneNumberMatch = originalPN.match(numberPattern);
        if (phoneNumberMatch) {
            phoneNumber = phoneNumberMatch.join("");
        }

        if (!phoneNumber || originalPN != phoneNumber) {
            return '';
        } else {
            // keep only the digits for this api call
            numberPattern = /\d+/g;
            phoneNumberMatch = phoneNumber.match(numberPattern);
            if (phoneNumberMatch) {
                phoneNumber = phoneNumberMatch.join("");
            }

            if (phoneNumber.length == 10) {
                return phoneNumber;
            } else {
                return '';
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/services/account/account.service.ts